import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { GoldLoader } from "../shared/Loaders";
import DocumentStatusPills from "../shared/DocumentStatusPills";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faFileSignature } from "@fortawesome/free-solid-svg-icons";
import "../css/DocumentsPage.css";
import { downloadEsignPdf } from "../../utils/esignDownloadUrl";
import { getAuthItem } from "../../utils/authStorage";

const API_URL = process.env.REACT_APP_API_URL;
const LEGALESIGN_COMPLETE_URL = "https://app1.legalesign.com/esign/complete";
const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Extract signer UUID from Legalesign signing URL (path contains signer id). */
function getSignerIdFromSigningUrl(signingUrl) {
  if (!signingUrl || typeof signingUrl !== "string") return null;
  const match = signingUrl.match(UUID_REGEX);
  return match ? match[0] : null;
}
const DocumentsPage = ({ personId: propPersonId, participantId: propParticipantId }) => {
  const personId = propPersonId || getAuthItem("personId");

  // FIX 1: Initialize from localStorage to prevent the "false -> true" toggle on load
  const [isMinor, setIsMinor] = useState(() => {
    const saved = localStorage.getItem("minorStatus");
    return saved === "Yes" || saved === "yes" || saved === "true";
  });

  const [isCaddie, setIsCaddie] = useState(() => {
    const saved = localStorage.getItem("isCaddie");
    return saved === "Yes" || saved === "yes" || saved === "true";
  });

  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [participantId, setParticipantId] = useState(propParticipantId || null);
  const [isDataLoaded, setIsDataLoaded] = useState(false); // Track if we've checked the API

  // Athlete titles kept; when group/role is caddie → Caddie / Caddie Minor (same minorStatus).
  const documentTitle = isCaddie
    ? (isMinor
      ? "IGF Registration Regulations Caddie (Minor)"
      : "IGF Registration Regulations Caddie")
    : isMinor
      ? "IGF Registration Regulations Athlete (Minor)"
      : "IGF Registration Regulations Athlete";

    const fetchDocument = useCallback(async () => {
      if (!personId) return;
      try {
        const res = await axios.get(`${API_URL}/esign-documents/athlete/${personId}`);
        const dataArray = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);

        if (dataArray.length > 0) {
          const found =
            dataArray.find(doc => doc.title === "IGF Registration Regulations" ||
              (doc.title && doc.title.includes("IGF Registration Regulations"))) ||
            dataArray[0];
          setDocument(found || null);
        } else {
          setDocument(null);
        }
      } catch (err) {
        console.error("Failed to fetch document:", err);
        setDocument(null);
      }
    }, [personId]);

  // Combined Effect to load all initial data
  useEffect(() => {
    const init = async () => {
      if (!personId) return;
      
      try {
        // 1. Fetch this person's participant (group_id + minorStatus) — not full list
        const res = await axios.get(`${API_URL}/ems/participants`, {
          params: { slim: true, personId },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        const userParticipant =
          list.find(
            (record) =>
              record.person_id &&
              String(record.person_id._id || record.person_id) === String(personId)
          ) || list[0];

        if (userParticipant) {
          setParticipantId(userParticipant._id);
          const mStatus = userParticipant?.person_data?.minorStatus;
          const isM =
            mStatus === "Yes" ||
            mStatus === "yes" ||
            mStatus === true ||
            String(mStatus).toLowerCase() === "yes";

          setIsMinor(isM);
          localStorage.setItem("minorStatus", isM ? "Yes" : "No");

          // Same group_id as backend esignService (single populated group).
          const g = userParticipant.group_id;
          const groupName = String(
            (Array.isArray(g) ? g[0]?.group_name : g?.group_name) || ""
          );
          const roleName = String(userParticipant?.role_id?.role_name || "");
          const accred = String(
            userParticipant?.accreditation_category_id?.category_name ||
              userParticipant?.accreditation_category_id?.function ||
              ""
          );
          const isC = [groupName, roleName, accred].some((n) =>
            n.toLowerCase().includes("caddie")
          );
          setIsCaddie(isC);
          localStorage.setItem("isCaddie", isC ? "Yes" : "No");
        }

        // 2. Sync real Legalesign status, then fetch the document
        try {
          await axios.post(`${API_URL}/esign-documents/sync-status`, { personId });
        } catch (e) {
          console.warn("sync-status:", e?.response?.data?.message || e?.message);
        }
        await fetchDocument();
      } catch (e) {
        console.error("Init error", e);
      } finally {
        setIsDataLoaded(true); // Now we are ready to show the UI
      }
    };

    init();
  }, [personId, fetchDocument]);

  // Tab return: ask backend for real Legalesign status (do not spoof completed).
  useEffect(() => {
    const onVisibility = async () => {
      if (typeof window === "undefined" || window.document.visibilityState !== "visible" || !personId) return;
      try {
        await axios.post(`${API_URL}/esign-documents/sync-status`, { personId });
      } catch (e) {
        console.warn("sync-status:", e?.response?.data?.error || e?.message);
      }
      fetchDocument();
    };
    window.document.addEventListener("visibilitychange", onVisibility);
    return () => window.document.removeEventListener("visibilitychange", onVisibility);
  }, [personId, fetchDocument]);

  /**
   * Sign: always generate/refresh unsigned docs so person_data is stamped into Legalesign.
   * Signed docs: open complete/download page only.
   */
  const handleSignOrDownload = async () => {
    if (!participantId) return alert("Participant ID not found.");
  
    if (document?.status === "SIGNED") {
      const signerId = getSignerIdFromSigningUrl(document.signingUrl) ||
        (document.legalesignDocId || "").replace(/^@+/, "").trim();
      if (signerId) return window.open(`${LEGALESIGN_COMPLETE_URL}/${signerId}/?r=1`, "_blank");
    }
  
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/esign-documents/generate`, {
        participantId: participantId,
        isCaddie,
        isMinor,
        expectedTitle: documentTitle,
      });
      if (response.data.signingUrl) {
        window.open(response.data.signingUrl, "_blank");
        fetchDocument();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to generate document.");
    } finally {
      setLoading(false);
    }
  };

  // Minor doc where the athlete/caddie has signed: the button opens the parent's signing link.
  const signers = document?.signerStatus || [];
  const awaitingParent =
    isMinor &&
    signers.some((s) => s.order === 0 && s.status === "SIGNED") &&
    signers.some((s) => s.role === "Parent" && s.status !== "SIGNED");

  const handleDownload = async () => {
    setLoading(true);
    try {
      await downloadEsignPdf(document?.legalesignDocId);
    } catch (err) {
      alert(err.response?.data?.error || err.message || "Download failed.");
    } finally {
      setLoading(false);
    }
  };

return (
  !isDataLoaded ? (
    <GoldLoader label="Loading..." variant="page" />
  ) : (
  <div className="p-6 mt-5">
    <div className="doc-table-container">
      <div className="doc-table-header">
        <div>Document Name</div>
        <div>Status</div>
        <div>Actions</div>
      </div>

      <div className="doc-row" style={{ padding: "5px" }}>
        <div>
          <span style={{ fontWeight: 600 }}>
            {documentTitle}
          </span>
        </div>

        {/* Status */}
        <div>
          <DocumentStatusPills
            status={document?.status}
            signers={document?.signerStatus}
            isMinor={isMinor}
            role={isCaddie ? "Caddie" : "Athlete"}
          />
        </div>

        {/* Actions: fully signed → Download; athlete signed (minor) → Parent sign; otherwise → Sign */}
        <div>
          {loading ? (
            <span style={{ color: "#999" }}>Processing...</span>
          ) : document?.status === "SIGNED" ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleDownload}
              disabled={loading}
              style={{
                background: "#D9A128",
                color: "#fff",
                border: "none",
                padding: "3px 10px",
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              <FontAwesomeIcon icon={faDownload} /> Download
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleSignOrDownload}
              disabled={loading}
              style={{
                background: "#D9A128",
                color: "#fff",
                border: "none",
                padding: "3px 10px",
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "12px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              <FontAwesomeIcon icon={faFileSignature} />{" "}
              {awaitingParent ? "Parent" : isCaddie ? "Caddie" : "Athlete"}: Click here to sign
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
  )
);
};

export default DocumentsPage;
